import {
  Badge,
  Card,
  CardBody,
  IconButton,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  useDisclosure,
} from "@chakra-ui/react";
import { CreditFacilityEventEditDateModal } from "features/credit-facility/CreditFacilityEditDateModal";
import { CreditFacilityStateMachineFieldsEnum } from "gen/model/creditFacilityStateMachineFieldsEnum";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import { EditIcon } from "shared/icons/EditIcon";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";

export interface CreditFacilityEventCardProps {
  creditFacilityEvent: FundFamilyEventData;
  onCloseComplete?: () => void;
  canEditEvent: boolean;
}

export const CreditFacilityEventHeaderCard: React.FC<
  CreditFacilityEventCardProps
> = ({ creditFacilityEvent, onCloseComplete, canEditEvent }) => {
  const { t } = useTranslation(["common", "creditFacility", "capitalActivity"]);
  const isDraftEvent: boolean =
    creditFacilityEvent.state == CreditFacilityStateMachineFieldsEnum.draft;
  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  return (
    <Card variant="stat" size="xs">
      <CardBody>
        <CardSectionGroup flexWrap="wrap">
          <CardSection flexGrow="1">
            <CardSectionBody alignItems="stretch">
              <StatGroup justifyContent="flex-start">
                <Stat>
                  <StatLabel>{t("creditFacility:effectiveDate")}</StatLabel>
                  <StatNumber>
                    <Badge>
                      {formatIsoDateToDisplay(
                        creditFacilityEvent.effective_date,
                      )}
                    </Badge>
                  </StatNumber>
                </Stat>
                {creditFacilityEvent.sub_type !==
                  FundFamilyEventSubTypeEnum.merge_tranches && (
                  <Stat>
                    <StatLabel>
                      {t("capitalActivity:dates.investorNoticeDate")}
                    </StatLabel>
                    <StatNumber>
                      <Badge>
                        {formatIsoDateToDisplay(
                          creditFacilityEvent.posted_date,
                        )}
                      </Badge>
                    </StatNumber>
                  </Stat>
                )}
                {(isDraftEvent || creditFacilityEvent.notes) && (
                  <Stat flex="1" flexBasis="max-content" maxWidth="400px">
                    <StatLabel>{t("common:notesOptional")}</StatLabel>
                    <StatNumber whiteSpace="normal">
                      {creditFacilityEvent.notes || "-"}
                    </StatNumber>
                  </Stat>
                )}
              </StatGroup>
              {isDraftEvent && canEditEvent && (
                <IconButton
                  p="24px"
                  aria-label="edit"
                  size="sm"
                  variant="ghost"
                  borderRadius={0}
                  icon={<EditIcon w="20px" h="20px" color="gray.400" />}
                  onClick={openEditDateModal}
                />
              )}
            </CardSectionBody>
          </CardSection>
        </CardSectionGroup>
      </CardBody>
      {isEditDateModalOpen && (
        <CreditFacilityEventEditDateModal
          fundFamilyId={creditFacilityEvent.fund_family_id}
          fundFamilyEventId={creditFacilityEvent.id!}
          onCloseComplete={() => {
            closeEditDateModal();
            if (onCloseComplete) {
              onCloseComplete();
            }
          }}
        />
      )}
    </Card>
  );
};
